<template>
  <aside
    class="flex flex-col w-full h-full bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950"
  >
    <div class="flex flex-col gap-2 items-center px-8 py-6">
      <img
        :src="logoURL"
        class="object-contain w-full drop-shadow-lg"
        alt="RSA"
      />

      <div class="flex flex-col items-center">
        <span
          class="text-base font-bold tracking-tight leading-none text-white whitespace-nowrap"
          >RSA Auto Network</span
        >
        <span
          class="mt-2 text-[10px] font-semibold tracking-[0.2em] uppercase text-white/40"
          >Operations</span
        >
      </div>
    </div>

    <nav class="flex overflow-y-auto flex-col flex-1 gap-0.5 px-2 pt-2 pb-5">
      <NuxtLink
        v-for="item in activeSidebarNavs"
        :key="item.name"
        :to="item.href"
        :class="
          classNames(
            'group relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-colors duration-150',
            item.current
              ? 'bg-white/5 text-white'
              : 'text-white/55 hover:text-white hover:bg-white/5',
          )
        "
      >
        <span
          v-if="item.current"
          class="absolute top-2 bottom-2 left-0 w-1 bg-red-500 rounded-r-full"
          aria-hidden="true"
        />
        <component
          :is="item.icon"
          :class="
            classNames(
              'size-5 shrink-0 transition-colors',
              item.current
                ? 'text-red-400'
                : 'text-white/40 group-hover:text-white/80',
            )
          "
          aria-hidden="true"
        />
        <span class="truncate">{{ item.name }}</span>
      </NuxtLink>
    </nav>

    <div
      class="flex gap-2 justify-between items-center px-3 py-3 border-t border-white/5"
    >
      <span class="text-[10px] font-semibold text-white/50 truncate"
        >RSA Web v2</span
      >

      <span class="flex gap-1.5 items-center shrink-0">
        <span
          :class="
            classNames('size-1.5 rounded-full', environmentBadge.dotClassName)
          "
          aria-hidden="true"
        />
        <span
          :class="
            classNames(
              'text-[10px] font-bold tracking-wider',
              environmentBadge.textClassName,
            )
          "
        >
          {{ environmentBadge.label }}
        </span>
      </span>
    </div>
  </aside>
</template>

<script lang="ts" setup>
import {
  BriefcaseIcon,
  BuildingOfficeIcon,
  ChartPieIcon,
  Cog6ToothIcon,
  HomeIcon,
  LifebuoyIcon,
  NewspaperIcon,
  UserIcon,
  MagnifyingGlassIcon,
} from "@heroicons/vue/24/outline";
import { IconCarCrash } from "@tabler/icons-vue";
import logoURL from "~/assets/image/logo/rsa-3D.svg";

interface Navigation {
  name: string;
  value: string;
  href: string;
  icon: any;
}

const runtimeConfig = useRuntimeConfig();
const route = useRoute();
const { allowedRoutes } = storeToRefs(useUserStore());

const environmentBadge = computed(() => {
  const env = runtimeConfig.public.environment;
  if (env === "production") {
    return {
      label: "PROD",
      textClassName: "text-red-300",
      dotClassName: "bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.7)]",
    };
  }
  if (env === "staging") {
    return {
      label: "STG",
      textClassName: "text-amber-300",
      dotClassName: "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.7)]",
    };
  }
  return {
    label: "DEV",
    textClassName: "text-emerald-300",
    dotClassName: "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.7)]",
  };
});

const sidebarNavs: Navigation[] = [
  { name: "Dashboard", value: "dashboard", href: "/dashboard", icon: HomeIcon },
  { name: "Case", value: "case", href: "/case", icon: BriefcaseIcon },
  {
    name: "Requests",
    value: "case-request",
    href: "/case-request",
    icon: IconCarCrash,
  },
  { name: "User", value: "user", href: "/user", icon: UserIcon },
  {
    name: "Company",
    value: "company",
    href: "/company",
    icon: BuildingOfficeIcon,
  },
  { name: "Track", value: "track", href: "/track", icon: LifebuoyIcon },
  { name: "News", value: "news", href: "/news", icon: NewspaperIcon },
  { name: "Report", value: "report", href: "/report", icon: ChartPieIcon },
  {
    name: "Trace",
    value: "trace",
    href: "/admin/trace",
    icon: MagnifyingGlassIcon,
  },
  { name: "Policy", value: "policy", href: "/policy", icon: UserIcon },
  {
    name: "Settings",
    value: "settings",
    href: "/settings",
    icon: Cog6ToothIcon,
  },
];

const activeSidebarNavs = computed(() => {
  return sidebarNavs
    .filter((item) => allowedRoutes.value.includes(item.value))
    .map((item) => ({
      ...item,
      current:
        route.path === item.href || route.path.startsWith(item.href + "/"),
    }));
});
</script>
